<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import InviteAnimation from "@/components/animations/InviteAnimation.vue";
import { useSnackStore } from "@/stores/snackStore";
import { useRejectInviteRecipientMutation } from "@/composables/models/invite/useInviteRecipient";
import { useSnackDialogStore } from "@/stores/useSnackDialogStore";

definePage({
  meta: {
    title: "features.cotreatmeInvitation.metaTitle",
    layout: "empty",
    requiresAuth: false,
  },
});

const { t } = useI18n();
const snackStore = useSnackStore();

const route = useRoute();
const router = useRouter();
const snackDialogStore = useSnackDialogStore();

const id = computed(() => ("id" in route.params ? String(route.params.id) : undefined));
const token = computed(() => route.query.token as string);

const { mutateAsync: mutateRejectAsync, isPending: isRejectPending } =
  useRejectInviteRecipientMutation(false);

const benefits = [
  {
    title: "features.cotreatmeInvitation.benefits.referrals.title",
    text: "features.cotreatmeInvitation.benefits.referrals.text",
  },
  {
    title: "features.cotreatmeInvitation.benefits.revenue.title",
    text: "features.cotreatmeInvitation.benefits.revenue.text",
  },
  {
    title: "features.cotreatmeInvitation.benefits.coTreat.title",
    text: "features.cotreatmeInvitation.benefits.coTreat.text",
  },
  {
    title: "features.cotreatmeInvitation.benefits.compliant.title",
    text: "features.cotreatmeInvitation.benefits.compliant.text",
  },
];

function redirectToWebsite() {
  globalThis.location.href = "https://www.cotreatme.com/";
}

async function onDecline() {
  if (id.value === undefined) return;
  await mutateRejectAsync(
    {
      id: id.value,
      data: {
        token: token.value,
      },
    },
    {
      onSuccess: () => {
        snackStore.showSuccess(t("features.cotreatmeInvitation.snackbar.declined"));

        snackDialogStore.show({
          title: t("generic.feedbackMessages.declineInvite.title"),
          text: t("generic.feedbackMessages.declineInvite.text"),
          confirmText: t("generic.action.close"),
          onConfirm: redirectToWebsite,
          type: "sad",
        });
      },
    }
  );
}

function onAccept() {
  router.push(
    `/cooperation/accept-invite/${id.value}/register?token=${token.value}&accepting=true`
  );
}
</script>

<template>
  <main
    class="ctm-invite tw:grid tw:[grid-template-columns:minmax(0,1.08fr)_minmax(0,1fr)] tw:min-h-screen tw:max-[920px]:[grid-template-columns:1fr]"
  >
    <section
      class="ctm-stage tw:relative tw:flex tw:flex-col tw:justify-between tw:overflow-hidden tw:text-[#eaf1fa] tw:max-[920px]:[min-height:auto] tw:max-[920px]:hidden"
      aria-hidden="false"
    >
      <div
        class="ctm-stage__glow tw:absolute tw:inset-0 tw:pointer-events-none"
        aria-hidden="true"
      />

      <header
        class="ctm-anim tw:flex tw:items-center tw:gap-3 tw:z-[1]"
        style="--d: 0ms"
      >
        <a href="https://cotreatme.com">
          <img
            src="/logo_color_negative.svg"
            :alt="t('generic.project.name')"
            class="tw:h-[22px] tw:w-auto tw:block"
          />
        </a>
      </header>

      <h1
        class="ctm-thesis ctm-anim tw:z-[1] tw:font-semibold tw:leading-[1.1] tw:tracking-[-0.025em] tw:m-0 tw:max-w-[17ch] tw:max-[920px]:max-w-none"
        style="--d: 90ms"
      >
        {{ t("features.cotreatmeInvitation.thesis.line1") }}
        <i18n-t
          keypath="features.cotreatmeInvitation.thesis.line2"
          scope="global"
          tag="span"
          class="tw:block"
        >
          <template #emphasis>
            <em class="ctm-thesis__em">{{ t("features.cotreatmeInvitation.thesis.emphasis") }}</em>
          </template>
        </i18n-t>
      </h1>

      <figure
        class="ctm-anim tw:z-[1] tw:m-0 tw:flex-1 tw:flex tw:flex-col tw:min-h-0"
        style="--d: 220ms"
        aria-hidden="true"
      >
        <invite-animation />
      </figure>
    </section>

    <section
      class="ctm-decide tw:grid tw:place-items-center"
      aria-labelledby="ctm-headline"
    >
      <div class="tw:w-[min(520px,100%)]">
        <h2
          id="ctm-headline"
          class="ctm-title ctm-anim tw:leading-[1.16] tw:tracking-[-0.02em] tw:font-semibold tw:[margin:0_0_12px]"
          style="--d: 240ms"
        >
          {{ t("features.cotreatmeInvitation.title.line1") }}
          <span class="tw:block">{{ t("features.cotreatmeInvitation.title.line2") }}</span>
        </h2>
        <p
          class="ctm-lead ctm-anim tw:text-[15.5px] tw:leading-[1.6] tw:m-0"
          style="--d: 320ms"
        >
          {{ t("features.cotreatmeInvitation.lead") }}
        </p>

        <ul class="tw:list-none tw:m-0 tw:p-0 tw:grid tw:gap-[14px]">
          <li
            v-for="(b, i) in benefits"
            :key="b.title"
            class="ctm-anim tw:flex tw:gap-3 tw:items-start"
            :style="{ '--d': `${420 + i * 90}ms` }"
          >
            <span
              class="ctm-benefits__check tw:flex-none tw:size-6 tw:rounded-[8px] tw:grid tw:place-items-center tw:mt-px"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                class="tw:size-[13px]"
              >
                <path
                  d="M3 8.5l3.2 3.2L13 5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <div>
              <h3 class="tw:text-[14.5px] tw:font-medium tw:[margin:0_0_3px]">
                {{ t(b.title) }}
              </h3>
              <p class="ctm-benefits__text tw:text-[13.5px] tw:leading-[1.5] tw:m-0">
                {{ t(b.text) }}
              </p>
            </div>
          </li>
        </ul>

        <!-- <i18n-t
          keypath="features.cotreatmeInvitation.trial.text"
          scope="global"
          tag="p"
          class="ctm-trial ctm-anim tw:py-[13px] tw:px-4 tw:rounded-[14px] tw:border tw:border-[#cfe2f7] tw:text-[13.5px] tw:leading-[1.55]"
          style="--d: 820ms"
        >
          <template #highlight>
            <strong>{{ t("features.cotreatmeInvitation.trial.highlight") }}</strong>
          </template>
        </i18n-t> -->

        <div
          class="ctm-anim tw:flex tw:gap-3 tw:mt-5 tw:flex-wrap tw:max-[560px]:flex-col-reverse"
          style="--d: 900ms"
          role="group"
          :aria-label="t('features.cotreatmeInvitation.actions.label')"
        >
          <button
            type="button"
            class="ctm-btn ctm-btn--decline tw:relative tw:text-[15.5px] tw:font-semibold tw:rounded-[14px] tw:py-[15px] tw:px-[26px] tw:cursor-pointer tw:border tw:border-transparent tw:overflow-hidden tw:[flex:0_1_auto] tw:bg-transparent tw:max-[560px]:w-full"
            :disabled="isRejectPending"
            @click="onDecline"
          >
            <span>{{ t("generic.action.decline") }}</span>
          </button>
          <button
            type="button"
            class="ctm-btn ctm-btn--accept tw:relative tw:text-[15.5px] tw:font-semibold tw:rounded-[14px] tw:py-[15px] tw:px-[26px] tw:cursor-pointer tw:border tw:border-transparent tw:overflow-hidden tw:[flex:1_1_220px] tw:max-h-[100px] tw:text-white"
            :disabled="isRejectPending"
            @click="onAccept"
          >
            <span
              class="ctm-btn__sheen tw:absolute tw:inset-0 tw:pointer-events-none"
              aria-hidden="true"
            />
            <span>{{ t("models.invitations.acceptInvitation") }}</span>
          </button>
        </div>

        <p
          class="ctm-footnote ctm-anim tw:text-[12.5px] tw:[margin:14px_0_0]"
          style="--d: 960ms"
        >
          {{ t("features.cotreatmeInvitation.footnote") }}
        </p>
      </div>
    </section>
  </main>
</template>

<style scoped>
.ctm-invite {
  --ctm-navy: #0b1626;
  --ctm-ink: #16263b;
  --ctm-ink-soft: #4d5d72;
  --ctm-paper: #f7f9fc;
  --ctm-blue: #1466c8;
  --ctm-blue-bright: #4da3ff;
  --ctm-mint: #19a88f;
  --ctm-mint-bright: #34d2b4;
  --ctm-blue-soft: #e3eefb;
  --ctm-mint-soft: #e0f4f0;
  --ctm-line: #d4dae4;
  color: var(--ctm-ink);
  background: var(--ctm-paper);
}

.ctm-anim {
  opacity: 0;
  transform: translateY(16px);
  animation: ctm-up 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  animation-delay: var(--d, 0ms);
}
@keyframes ctm-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ctm-stage {
  gap: clamp(20px, 3vh, 36px);
  margin: clamp(12px, 1.5vw, 20px);
  padding: clamp(28px, 4vw, 56px);
  border-radius: clamp(16px, 1.6vw, 28px);
  background:
    radial-gradient(120% 90% at 85% -10%, #16345c 0%, transparent 55%),
    radial-gradient(110% 80% at -10% 110%, #0e3b33 0%, transparent 55%), var(--ctm-navy);
}

.ctm-stage__glow {
  background: radial-gradient(40% 35% at 50% 55%, rgba(52, 210, 180, 0.16) 0%, transparent 70%);
  animation: ctm-breathe 7s ease-in-out infinite;
}
@keyframes ctm-breathe {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

.ctm-thesis {
  font-size: clamp(36px, 4.5vw, 62px);
}

.ctm-thesis__em {
  font-style: normal;
  background: linear-gradient(95deg, var(--ctm-blue-bright), var(--ctm-mint-bright));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ctm-hueslide 5s ease-in-out infinite;
}
@keyframes ctm-hueslide {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.ctm-decide {
  padding: clamp(28px, 4vw, 56px);
  background: var(--ctm-paper);
}

.ctm-eyebrow {
  color: var(--ctm-blue);
  background: var(--ctm-blue-soft);
}

.ctm-title {
  font-size: clamp(24px, 2.6vw, 34px);
}
@media (max-width: 920px) {
  .ctm-title {
    font-size: clamp(30px, 10vw, 40px);
  }
}

.ctm-lead {
  color: var(--ctm-ink-soft);
  margin-bottom: clamp(20px, 3vh, 28px);
}

.ctm-benefits__check {
  background: var(--ctm-mint-soft);
  color: var(--ctm-mint);
}

.ctm-benefits__text {
  color: var(--ctm-ink-soft);
}

.ctm-trial {
  margin-top: clamp(18px, 2.5vh, 24px);
  background: var(--ctm-blue-soft);
}

.ctm-btn {
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
}
.ctm-btn:focus-visible {
  outline: 3px solid var(--ctm-blue);
  outline-offset: 2px;
}
.ctm-btn:disabled {
  cursor: default;
  opacity: 0.65;
}

.ctm-btn--accept {
  background: linear-gradient(100deg, var(--ctm-blue), #0e8f7a);
  box-shadow: 0 10px 26px rgba(20, 102, 200, 0.32);
}
.ctm-btn--accept:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 32px rgba(20, 102, 200, 0.4);
}
.ctm-btn--accept:not(:disabled):active {
  transform: translateY(0);
}

.ctm-btn__sheen {
  background: linear-gradient(
    115deg,
    transparent 35%,
    rgba(255, 255, 255, 0.35) 50%,
    transparent 65%
  );
  transform: translateX(-120%);
  animation: ctm-sheen 3.8s ease-in-out infinite;
  animation-delay: 1.6s;
}
@keyframes ctm-sheen {
  0%,
  55% {
    transform: translateX(-120%);
  }
  75%,
  100% {
    transform: translateX(120%);
  }
}

.ctm-btn--decline {
  color: var(--ctm-ink-soft);
  border-color: var(--ctm-line);
}
.ctm-btn--decline:not(:disabled):hover {
  background: #eef1f6;
  color: var(--ctm-ink);
}

.ctm-footnote {
  color: var(--ctm-ink-soft);
}

@media (prefers-reduced-motion: reduce) {
  .ctm-anim {
    animation: none;
    opacity: 1;
    transform: none;
  }
  .ctm-stage__glow,
  .ctm-btn__sheen,
  .ctm-thesis__em {
    animation: none;
  }
  .ctm-btn {
    transition: none;
  }
}
</style>
